/**
 * The online table's *connection* state machine — everything that can cover the
 * table without being a mahjong rule. It is deliberately a separate concern
 * from `MahjongState`: the server keeps sending snapshots while a peer is away,
 * and this is what says the table is frozen anyway.
 *
 * Pure except for its own countdown ticker, and driven entirely by method calls
 * — `NetworkGameController` translates the wire into them, so this file can be
 * unit-tested without a socket. The states mirror checkers', backgammon's and
 * domino's, because `@bn-games/ui-shell`'s `GameStateOverlay` renders exactly
 * those seven; the *union* is per-app (each game owns its own), the vocabulary
 * is shared.
 *
 * Mahjong's one departure: the disconnect states name no opponent. A four-seat
 * table has three of them and `OPPONENT_DISCONNECTED` carries no seat, so the
 * copy says "a player" rather than inventing a name (see `ui/onlineOverlayLabels.ts`).
 */

/** Canvas-covering states. `none` means the table is playable. */
export type OnlineOverlay =
  | 'none'
  | 'opponent_disconnected'
  | 'reconnecting_self'
  | 'game_disconnected'
  | 'rematch_offered'
  | 'rematch_requested'
  | 'rematch_declined'
  | 'game_over';

export type GameOverResult = 'win' | 'loss' | 'draw';

export interface OverlayView {
  readonly overlay: OnlineOverlay;
  /** Absolute epoch-ms deadline of the reconnect grace window, or `null`. */
  readonly gracePeriodEndsAt: number | null;
  readonly gameOverResult: GameOverResult | null;
}

export const NO_OVERLAY: OverlayView = {
  overlay: 'none',
  gracePeriodEndsAt: null,
  gameOverResult: null,
};

/**
 * How long our *own* reconnect may take before we call it dead. The server
 * stamps the deadline for a peer's drop (`OPPONENT_DISCONNECTED`); for our own
 * we cannot be told anything while offline, so the client mirrors the room
 * server's `RECONNECT_WINDOW_MS`.
 */
export const RECONNECT_GRACE_MS = 300_000;

/** Countdown redraw cadence — the label shows whole seconds. */
const COUNTDOWN_TICK_MS = 1_000;

/** The two states whose body text counts down. */
function isWaiting(overlay: OnlineOverlay): boolean {
  return overlay === 'opponent_disconnected' || overlay === 'reconnecting_self';
}

/** The three states that mean "the table is frozen, act on nothing". */
export function isPausedOverlay(overlay: OnlineOverlay): boolean {
  return isWaiting(overlay) || overlay === 'game_disconnected';
}

export class OverlayMachine {
  private readonly onChange: () => void;
  private state: OverlayView = NO_OVERLAY;
  private ticker: ReturnType<typeof setInterval> | null = null;

  constructor(onChange: () => void) {
    this.onChange = onChange;
  }

  view(): OverlayView {
    return this.state;
  }

  // --- connection ---

  /** A peer dropped; the server tells us when its grace window expires. */
  opponentDisconnected(gracePeriodEndsAt: number): void {
    this.set('opponent_disconnected', { gracePeriodEndsAt });
  }

  opponentReconnected(): void {
    if (this.state.overlay !== 'opponent_disconnected') return;
    this.set('none', { gracePeriodEndsAt: null });
  }

  /** Our own socket went away. A closed game stays closed. */
  selfDisconnected(now: number): void {
    if (this.state.overlay === 'game_disconnected') return;
    this.set('reconnecting_self', { gracePeriodEndsAt: now + RECONNECT_GRACE_MS });
  }

  /** The socket is back, but the room has not confirmed the seat yet — so the
   *  overlay stays up, without a countdown, until `RECONNECT_OK` lands. */
  selfConnected(): void {
    if (this.state.overlay !== 'reconnecting_self') return;
    this.set('reconnecting_self', { gracePeriodEndsAt: null });
  }

  /** The room confirmed the seat: whatever we were waiting on is over. */
  reconnected(): void {
    const overlay = this.state.overlay === 'reconnecting_self' ? 'none' : this.state.overlay;
    this.set(overlay, { gracePeriodEndsAt: null });
  }

  /** Terminal: the grace window elapsed, or the room refused the rejoin. */
  disconnected(): void {
    this.set('game_disconnected', { gracePeriodEndsAt: null });
  }

  // --- rematch ---

  /** We asked; we are waiting on the others. */
  rematchOffered(): void {
    this.set('rematch_offered', {});
  }

  /** Somebody else asked. */
  rematchRequested(): void {
    this.set('rematch_requested', {});
  }

  rematchDeclined(): void {
    this.set('rematch_declined', {});
  }

  /** A fresh table — the plan's open question #3, answered server-side. */
  rematchStarted(): void {
    this.set('none', { gameOverResult: null });
  }

  // --- terminals ---

  gameOver(result: GameOverResult): void {
    if (this.state.overlay === 'game_disconnected') return;
    this.set('game_over', { gameOverResult: result });
  }

  /** Dismiss a state the player may walk away from (never a disconnect one). */
  dismiss(): void {
    if (isPausedOverlay(this.state.overlay)) return;
    this.set('none', { gameOverResult: null });
  }

  dispose(): void {
    this.stopTicker();
  }

  private set(overlay: OnlineOverlay, patch: Partial<OverlayView>): void {
    const next: OverlayView = { ...this.state, ...patch, overlay };
    if (
      next.overlay === this.state.overlay &&
      next.gracePeriodEndsAt === this.state.gracePeriodEndsAt &&
      next.gameOverResult === this.state.gameOverResult
    ) {
      return;
    }
    this.state = next;
    this.syncTicker();
    this.onChange();
  }

  private syncTicker(): void {
    const wants = this.state.gracePeriodEndsAt !== null && isWaiting(this.state.overlay);
    if (wants && this.ticker === null) {
      this.ticker = setInterval(() => this.tick(), COUNTDOWN_TICK_MS);
    } else if (!wants) {
      this.stopTicker();
    }
  }

  /**
   * Redraw the countdown, or end the wait once it has run down.
   *
   * **Which wait it was decides what "run down" means**, and mahjong differs
   * from the two-player games this machine was ported from. There, a peer who
   * never returns ends the game, so both waits collapsed to `game_disconnected`.
   * Here the table has four seats and server-driven bots: an abandoned seat
   * becomes a bot (`server/…/SeatPresence.ts`) and the hand plays on, so our own
   * table is *fine* and must go back to `none`.
   *
   * Collapsing the two was a table-bricking bug, not a cosmetic one:
   * `game_disconnected` is terminal (`dismiss` refuses it, `opponentReconnected`
   * cannot clear it) and `isPausedOverlay` makes `OnlineDriver` drop every
   * intent, so a peer's grace expiring left us unable to play — permanently, and
   * even if that peer walked straight back in.
   */
  private tick(): void {
    const endsAt = this.state.gracePeriodEndsAt;
    if (endsAt === null || Date.now() < endsAt) {
      this.onChange();
      return;
    }
    if (this.state.overlay === 'reconnecting_self') this.disconnected();
    else this.set('none', { gracePeriodEndsAt: null });
  }

  private stopTicker(): void {
    if (this.ticker === null) return;
    clearInterval(this.ticker);
    this.ticker = null;
  }
}
